import {
  LdLoading,
  LdPagination,
  LdRadio,
  LdTable,
  LdTableBody,
  LdTableCaption,
  LdTableCell,
  LdTableHead,
  LdTableRow,
  LdTableToolbar,
} from "@liquid/react";
import { DownloadExcelButton } from "@src/components";
import { CRIPSER_PATH } from "@src/config";
import { ResultTableProps } from "@src/types";
import { JSX, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Highlighter from "react-highlight-words";
import { DownloadExcelOffTarget } from "../Download/DownloadOffTarget/DownloadExcelOffTarget";
import useIframeHeight from "../IframeHeight/Iframe";
import { TableFilter } from "../TableFilter";
import {
  StyledTableHeader,
  StyledTableLabel,
  StyledTableRow,
} from "./ResultTable.style";
import { ROWS_PER_PAGE } from "@src/config";

const ResultTable = ({
  data,
  tableDef,
  enableDownload = false,
  offTargetDownload = false,
  enableFilter,
  selectedIDs,
  summaryData,
  labelText,
  removeToolbar,
  selectedMode,
  pagination = true,
  onPrimerDesignSelected,
}: ResultTableProps) => {
  const [elements, setElements] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage] = useState(Number(ROWS_PER_PAGE[0]));
  const [sortOrder, setSortOrder] = useState<any>(null);

  const { uniqueKey, multiple, single } = tableDef;

  // new update - replace elementsInPage with state
  const [elementsInPage, setElementsInPage] = useState([]);

  useEffect(() => {
    setElements([]);
    setTimeout(() => {
      setElements(data);
    }, 10);
  }, [data]);

  useEffect(() => {
    if (selectedIDs) {
      if (!selectedIDs?.gRNA_id?.length) {
        onSelectAll({ detail: { selected: false } });
      } else {
        const idx = elements.findIndex((item: any) =>
          selectedIDs.gRNA_id.includes(item.gRNA_id)
        );
        if (idx >= 0) {
          onSelect({
            detail: {
              rowIndex: idx - currentPage * rowsPerPage,
              selected: selectedIDs.checked,
            },
          });
        }
      }
    }
  }, [selectedIDs]);

  //Adding logic for height
  useIframeHeight(CRIPSER_PATH);

  const elementsSorted = useMemo(() => {
    if (!elements || !sortOrder) return elements;

    const sortKeys = tableDef.columns.map((col: any) =>
      col.children
        ? col.children.map((ccol: any) => ccol.sortable && ccol.dataKey).flat()
        : col.sortable && col.dataKey
    );
    return [...elements].sort((a, b) => {
      const key =
        sortOrder.pIndex !== null
          ? sortKeys[sortOrder.pIndex][sortOrder.columnIndex]
          : sortKeys[sortOrder.columnIndex];
      const val1: string = (sortOrder.sortOrder === "asc" ? a : b)[key];
      const val2 = (sortOrder.sortOrder === "asc" ? b : a)[key];
      const str1 = "" + val1 || "";
      const str2 = "" + val2 || "";
      const num1 = parseFloat(str1.replaceAll(/,/g, ""));
      const num2 = parseFloat(str2.replaceAll(/,/g, ""));
      if (!isNaN(num1) && !isNaN(num2)) {
        return num1 - num2;
      }
      return str1.localeCompare(str2);
    });
  }, [elements, sortOrder, tableDef.columns]);

  // new update - useeffect to update elementsInPage when data sorted

  useEffect(() => {
    // Calculate the slice of elements to display
    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    let timeoutId: any = null;
    // Only update elementsInPage if elementsSorted length more than 5
    if (elementsSorted.length <= 5) {
      setElementsInPage(elementsSorted.slice(startIndex, endIndex));
    } else {
      setElementsInPage([]);
      timeoutId = setTimeout(() => {
        setElementsInPage(elementsSorted.slice(startIndex, endIndex));
      }, 10);
    }
    // Cleanup the timeout on unmount or dependency change
    return () => clearTimeout(timeoutId);
  }, [currentPage, elementsSorted, rowsPerPage]);

  const totalSelected = useMemo(() => {
    return elements.filter((e: any) => e.selected).length;
  }, [elements]);

  const allSelected = useMemo(() => {
    return totalSelected === elements.length;
  }, [elements, totalSelected]);

  const someSelected = useMemo(() => {
    return totalSelected > 0 && totalSelected < elements.length;
  }, [elements, totalSelected]);

  const totalPages = useMemo(() => {
    return Math.ceil(elements.length / rowsPerPage) || Infinity;
  }, [elements, rowsPerPage]);

  const tableRef = useRef<any>();

  function onPageChangeEvent(ev: any) {
    setCurrentPage(ev.detail);
  }

  const onSort = useCallback(
    (ev: any) => {
      let pIndex = null;
      ev.target.classList.forEach((entry: string) => {
        if (entry.includes("parent-data-index")) {
          pIndex = Number(entry.split("-").pop());
        }
      });
      setCurrentPage(0);
      setSortOrder({ ...ev.detail, pIndex });
    },
    [setCurrentPage, setSortOrder]
  );

  useEffect(() => {
    const currentRef = tableRef.current;
    if (!currentRef) return;

    currentRef.addEventListener("ldTableSort", onSort, true);

    return () => {
      currentRef.removeEventListener("ldTableSort", onSort, true);
    };
  }, [onSort]);

  const onSelect = useCallback(
    (ev: any) => {
      const selected =
        typeof ev.detail.selected === "boolean"
          ? ev.detail.selected
          : ev.detail;
      const elementNumber = single
        ? ev.target.value
        : ev.currentTarget
          ? elementsInPage[ev.detail.rowIndex][uniqueKey]
          : elements[ev.detail.rowIndex][uniqueKey];
      const updatedElements = elements.map((e: any) => {
        return {
          ...e,
          selected:
            e[uniqueKey] === elementNumber
              ? selected
              : multiple
                ? e.selected
                : undefined,
        };
      });
      if (tableDef.onRowSelect && typeof tableDef.onRowSelect === "function") {
        tableDef.onRowSelect(
          updatedElements.filter((el: any) => el.selected && el)
        );
      }
      setElements(updatedElements);
    },
    [elementsInPage, elements, tableDef]
  );

  const onSelectAll = useCallback(
    (ev: { detail: { selected: boolean }; stopPropagation?: any }) => {
      if (!multiple) {
        // if multiple option is not set, dont do anything
        ev?.stopPropagation();
      } else {
        const updatedElements = elements.map((e: any) => {
          return {
            ...e,
            selected: ev?.detail.selected,
          };
        });
        setElements(updatedElements);
      }
    },
    [elements]
  );

  useEffect(() => {
    const current = tableRef.current;
    if (!current) return;
    current.addEventListener("ldTableSort", onSort, true);

    return () => {
      current.removeEventListener("ldTableSort", onSort, true);
    };
  }, []);

  function renderColumns(element: any, index: number): JSX.Element {
    const rowKey = `${uniqueKey}-${index}`;
    return (
      <LdTableRow
        selectable={multiple}
        selected={element.selected}
        onLdTableSelect={onSelect}
        key={rowKey}
      >
        {single ? (
          <LdTableCell key={`radio-${index}`}>
            <LdRadio
              checked={element.selected}
              value={element[uniqueKey]}
              name="radio"
              onLdchange={onSelect}
            />
          </LdTableCell>
        ) : undefined}
        {tableDef?.columns.map((col: any, cIndex: number) => {
          if (col.children && col.children.length) {
            return col.children.map((col2: any, cIndex2: number) => {
              return (
                <LdTableCell key={`${rowKey}-${cIndex}-${cIndex2}`}>
                  {col2.render && typeof col2.render === "function"
                    ? col2.render(element[col2.dataKey], element, index)
                    : element[col2.dataKey]}
                </LdTableCell>
              );
            });
          }

          if (col.render && typeof col.render === "function") {
            return (
              <LdTableCell key={`${rowKey}-${cIndex}`}>
                {col.render(element[col.dataKey], element, index)}
              </LdTableCell>
            );
          } else {
            return (
              <LdTableCell key={`${rowKey}-${cIndex}`}>
                {col.dataKey2 ? (
                  <>
                    <pre>{element[col.dataKey]}</pre>
                    <pre>{element[col.dataKey2]}</pre>
                  </>
                ) : col.dataKey === "transcript_status" ? (
                  <Highlighter
                    highlightStyle={{
                      color: "green",
                      backgroundColor: "transparent",
                    }}
                    searchWords={["Mane Select"]}
                    autoEscape={true}
                    textToHighlight={element[col.dataKey]}
                  />
                ) : (
                  element[col.dataKey]
                )}
              </LdTableCell>
            );
          }
        })}
      </LdTableRow>
    );
  }

  return (
    <>
      {labelText && <StyledTableLabel>{labelText}</StyledTableLabel>}
      <LdTable ref={tableRef} style={{ maxHeight: "26rem" }}>
        {!removeToolbar && (
          <LdTableToolbar slot="toolbar">
            {tableDef.tableTitle && (
              <LdTableCaption>{tableDef.tableTitle}</LdTableCaption>
            )}
            {totalPages && pagination && (
              <>
                <LdPagination
                  offset={1}
                  size="sm"
                  length={totalPages}
                  selectedIndex={currentPage}
                  onLdchange={onPageChangeEvent}
                  style={{ marginLeft: "auto" }}
                />
              </>
            )}

            {enableFilter ? (
              <TableFilter
                onSelect={enableFilter.onSelect}
                options={enableFilter.options}
              />
            ) : undefined}
            {enableDownload && (
              <DownloadExcelButton
                data={elements}
                selectedMode={selectedMode}
                onPrimerDesignSelected={onPrimerDesignSelected}
              />
            )}
            {offTargetDownload && (
              <DownloadExcelOffTarget
                data={[data]}
                summaryData={[summaryData]}
              />
            )}
          </LdTableToolbar>
        )}

        <LdTableHead>
          <StyledTableRow
            selectable={multiple}
            selected={allSelected}
            indeterminate={multiple && someSelected}
            onLdTableSelectAll={onSelectAll}
          >
            {single ? <StyledTableHeader></StyledTableHeader> : undefined}
            {tableDef?.columns.map((col: any, index: number) => {
              return (
                <StyledTableHeader
                  scope={col.children ? "centeralign" : ""}
                  rowspan={col.children ? 1 : 2}
                  colspan={col.children ? 4 : 1}
                  key={`${index}-${col.key}`}
                  sortable={col.sortable || false}
                >
                  {col.name}
                </StyledTableHeader>
              );
            })}
          </StyledTableRow>
          <StyledTableRow scope="down" selectable={multiple}>
            {tableDef?.columns.map((col: any, index: number) => {
              return col?.children
                ? col?.children.map((col2: any, index2: number) => {
                  return (
                    <StyledTableHeader
                      scope="down"
                      key={`${index}-${col2.key}`}
                      sortable={col2.sortable || false}
                      className={`parent-data-index-${index}`}
                    >
                      {col2.name}
                    </StyledTableHeader>
                  );
                })
                : undefined;
            })}
          </StyledTableRow>
        </LdTableHead>
        <LdTableBody>
          {elementsInPage.length > 0 ? (
            elementsInPage.map((element: any, index: number) =>
              renderColumns(element, index)
            )
          ) : (
            <LdTableRow>
              <LdTableCell colspan={10} style={{ textAlign: "center" }}>
                <LdLoading />
              </LdTableCell>
            </LdTableRow>
          )}
        </LdTableBody>
      </LdTable>
    </>
  );
};

export { ResultTable };